import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSaveAndGenerateMarketingMaterial, useSaveMarketingMaterial, useSaveMarketingPropertyDetails, useSavedMarketingMaterials } from 'dataQueries/visualsMarketing.query'

import { VisualsMarketingTargetPlatform } from '../VisulalsMarketing.constants'
import constate from 'constate'
import { logAnalyticsEvent } from 'utils/analytics'
import { useQueryClient } from '@tanstack/react-query'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualsMarketingForm } from './VisualsMarketing.form'

export const [MarketingMaterialsContextProvider, useMarketingMaterials] = constate(({ realtyId }: { realtyId: string }) => {


  const client = useQueryClient()
  const { currentUserWorkspace, baseUserData, userPropertyWorkspaceId } = useUserData()

  const { getSerializedData } = useVisualsMarketingForm()

  const savePropertyDetails = useSaveMarketingPropertyDetails()

  const saveBrochure = useSaveMarketingMaterial()
  const saveLinkedin = useSaveMarketingMaterial()
  const saveInstagram = useSaveMarketingMaterial()

  const generateBrochure = useSaveAndGenerateMarketingMaterial(VisualsMarketingTargetPlatform.REAL_ESTATE_BROCHURE)
  const generateInstagram = useSaveAndGenerateMarketingMaterial(VisualsMarketingTargetPlatform.INSTAGRAM)
  const generateLinkedin = useSaveAndGenerateMarketingMaterial(VisualsMarketingTargetPlatform.LINKEDIN)

  const savedMarketingMaterials = useSavedMarketingMaterials(realtyId, userPropertyWorkspaceId)
  const marketingMaterials = useMemo(() => savedMarketingMaterials.data || {}, [savedMarketingMaterials.data])

  /** Map of generate mutations */
  const generators = useMemo<Record<VisualsMarketingTargetPlatform, typeof generateInstagram>>(() => ({
    [VisualsMarketingTargetPlatform.INSTAGRAM]: generateInstagram,
    [VisualsMarketingTargetPlatform.LINKEDIN]: generateLinkedin,
    [VisualsMarketingTargetPlatform.REAL_ESTATE_BROCHURE]: generateBrochure,
  }), [generateBrochure, generateInstagram, generateLinkedin])

  /** Map of edit mutations */
  const editors = useMemo<Record<VisualsMarketingTargetPlatform, typeof saveBrochure>>(() => ({
    [VisualsMarketingTargetPlatform.INSTAGRAM]: saveInstagram,
    [VisualsMarketingTargetPlatform.LINKEDIN]: saveLinkedin,
    [VisualsMarketingTargetPlatform.REAL_ESTATE_BROCHURE]: saveBrochure,
  }), [saveBrochure, saveInstagram, saveLinkedin])

  /** Map of opened edit modes */
  const [openedEdits, setOpenedEdits] = useState<Record<VisualsMarketingTargetPlatform, boolean>>({
    [VisualsMarketingTargetPlatform.INSTAGRAM]: false,
    [VisualsMarketingTargetPlatform.LINKEDIN]: false,
    [VisualsMarketingTargetPlatform.REAL_ESTATE_BROCHURE]: false,
  })

  /** Map of values for editing inputs */
  const [editForms, setEditForms] = useState<Record<VisualsMarketingTargetPlatform, string>>({
    [VisualsMarketingTargetPlatform.INSTAGRAM]: '',
    [VisualsMarketingTargetPlatform.LINKEDIN]: '',
    [VisualsMarketingTargetPlatform.REAL_ESTATE_BROCHURE]: '',
  })

  /** Flag for initial hydration */
  const isHydrated = useRef(false)

  /** Handler for edit form field change */
  const onEditFieldChange = useCallback((platform: VisualsMarketingTargetPlatform, text: string) => {
    setEditForms((prev) => ({
      ...prev,
      [platform]: text
    }))
  }, [])

  /** Flips openedEdit switch for a provided platform to true and prefills data from fetched materials */
  const openEdit = useCallback((platform: VisualsMarketingTargetPlatform) => {
    setEditForms((prev) => ({
      ...prev,
      [platform]: marketingMaterials[platform]?.text || ''
    }))

    setOpenedEdits((prev) => ({
      ...prev,
      [platform]: true
    }))
  }, [marketingMaterials])

  /** Flips openedEdit switch for a provided platform to false */
  const closeEdit = useCallback((platform: VisualsMarketingTargetPlatform) => {
    setOpenedEdits((prev) => ({
      ...prev,
      [platform]: false
    }))
  }, [])

  /** Starts generateText flow for a target platform. If shouldSave is false, the saveDetails step will be skipped */
  const generateText = useCallback((platform: VisualsMarketingTargetPlatform, isReGenerate: boolean, shouldSave = true) => {
    if (!realtyId) return

    const data = getSerializedData()
    if (!data) return

    if (isReGenerate) {
      logAnalyticsEvent('Re-Generate Text', {
        userEmail: baseUserData?.email,
        planId: currentUserWorkspace?.id,
        planName: currentUserWorkspace?.name,
        targetPlatform: platform,
        realtyId,
      })
    }

    generators[platform]?.mutate({
      data: data.propertyInfo,
      payload: data.textSettings,
      realtyId,
      workspaceId: userPropertyWorkspaceId,
      shouldSave,
    })
  }, [realtyId, getSerializedData, generators, userPropertyWorkspaceId, baseUserData?.email, currentUserWorkspace?.id, currentUserWorkspace?.name])

  /** Saves property details */
  const saveDetails = useCallback((onSuccess?: () => void) => {
    if (!realtyId) return

    const data = getSerializedData()
    if (!data) return

    savePropertyDetails.mutate({
      realtyId,
      workspaceId: userPropertyWorkspaceId,
      data: data.propertyInfo,
      onSuccess,
    })
  }, [realtyId, getSerializedData, savePropertyDetails, userPropertyWorkspaceId])

  /** Saves changes of target platform text and closes Edit mode on success */
  const savePost = useCallback((platform: VisualsMarketingTargetPlatform) => {
    if (!realtyId) return
    const data = marketingMaterials[platform]
    if (!data?.id) return

    editors[platform].mutate({
      realtyId,
      workspaceId: userPropertyWorkspaceId!,
      id: data.id,
      text: editForms[platform],
      platform,
      onSuccess: () => closeEdit(platform)
    })

  }, [realtyId, marketingMaterials, editors, userPropertyWorkspaceId, editForms, closeEdit])

  // INIT HYDRATIONS
  useEffect(() => {
    if (savedMarketingMaterials.isFetched && !isHydrated.current) {
      isHydrated.current = true
    }
  }, [savedMarketingMaterials.isFetched])

  // PURGE ALL ON DESTROY
  useEffect(() => {
    return () => {
      savePropertyDetails.reset()
      saveBrochure.reset()
      saveLinkedin.reset()
      saveInstagram.reset()
      generateBrochure.reset()
      generateInstagram.reset()
      generateLinkedin.reset()
      client.resetQueries({ queryKey: [EntityKeys.VISUALS_MARKETING_PROPERTY_DETAILS, QueryType.GET, { realtyId }] })
      isHydrated.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    marketingMaterials,
    generators,
    editors,
    openedEdits,
    editForms,
    savePropertyDetails,
    savedMarketingMaterials,
    saveDetails,
    generateText,
    openEdit,
    closeEdit,
    savePost,
    onEditFieldChange,
  }
})
